// frontend/src/services/auth.service.ts
import api from './api';
import { User, RegisterResponse } from '../types';
import { AxiosError } from 'axios';

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  planType?: string;
  planDuration?: string;
  currency?: string;
}

interface LoginData {
  email: string;
  password: string;
}

const AuthService = {
  login: async (loginData: LoginData): Promise<RegisterResponse> => {
    try {
      console.log('Sending login request:', loginData);
      const response = await api.post<RegisterResponse>('/auth/login', loginData);
      console.log('Login response:', response.data);
      
      // If user is verified, store authentication data
      if (response.data.accessToken && response.data.user?.isVerified) {
        localStorage.setItem('token', response.data.accessToken);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        console.log('Stored in localStorage:', { token: response.data.accessToken, user: response.data.user });
      } else if (!response.data.user?.isVerified) {
        // User is not verified, store email for verification page
        localStorage.setItem('pendingVerificationEmail', loginData.email);
        console.log('User not verified, stored email for verification page:', loginData.email);
      }
      
      return response.data;
    } catch (error) {
      console.error('Login API error:', error);
      
      // Special handling for verification errors
      if (error instanceof AxiosError && error.response?.status === 401) {
        const errorMessage = error.response.data.message || '';
        if (errorMessage.includes('verify') || errorMessage.includes('verification')) {
          // Store email for verification page
          localStorage.setItem('pendingVerificationEmail', loginData.email);
          console.log('Verification error, stored email for verification page:', loginData.email);
        }
      }
      
      if (error instanceof AxiosError && error.response) {
        throw new Error(error.response.data.message || 'Invalid credentials');
      }
      throw error instanceof Error ? error : new Error('An unexpected error occurred');
    }
  },

  register: async (registerData: RegisterData): Promise<RegisterResponse> => {
    try {
      console.log('Sending register request:', registerData);
      const response = await api.post<RegisterResponse>('/auth/register', registerData);
      console.log('Register response:', response.data);
      
      // After registration, we don't store token because email verification is required
      // Instead, just store the email for verification page
      localStorage.setItem('pendingVerificationEmail', registerData.email);
      console.log('Registered, stored email for verification page:', registerData.email);
      
      // Return the response even if we don't store the token
      return response.data;
    } catch (error) {
      console.error('Register API error:', error);
      if (error instanceof AxiosError && error.response) {
        throw new Error(error.response.data.message || 'Registration failed');
      }
      throw error instanceof Error ? error : new Error('An unexpected error occurred');
    }
  },

  logout: (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('pendingVerificationEmail');
  },

  getCurrentUser: (): User | null => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return token !== null && user !== null;
  },

  getProfile: async (): Promise<User> => {
    try {
      const response = await api.get<User>('/auth/profile');
      
      // Update stored user data with latest from server
      localStorage.setItem('user', JSON.stringify(response.data));
      
      return response.data;
    } catch (error) {
      console.error('Get profile API error:', error);
      if (error instanceof AxiosError && error.response?.status === 401) {
        // Token might be invalid, clear authentication
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      throw error;
    }
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string | string[] }>;
      if (axiosError.response && axiosError.response.data && axiosError.response.data.message) {
        const message = Array.isArray(axiosError.response.data.message)
          ? axiosError.response.data.message.join(', ')
          : axiosError.response.data.message;
        throw new Error(message);
      }
      throw error instanceof Error ? error : new Error('An unexpected error occurred');
    }
  },
  
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>('/auth/verify', { token });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string | string[] }>;
      if (axiosError.response && axiosError.response.data && axiosError.response.data.message) {
        const message = Array.isArray(axiosError.response.data.message)
          ? axiosError.response.data.message.join(', ')
          : axiosError.response.data.message;
        throw new Error(message);
      }
      throw error instanceof Error ? error : new Error('An unexpected error occurred');
    }
  },
  
  resendVerification: async (email: string): Promise<{ message: string }> => {
    try {
      const response = await api.post<{ message: string }>('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string | string[] }>;
      if (axiosError.response && axiosError.response.data && axiosError.response.data.message) {
        const message = Array.isArray(axiosError.response.data.message)
          ? axiosError.response.data.message.join(', ')
          : axiosError.response.data.message;
        throw new Error(message);
      }
      throw error instanceof Error ? error : new Error('An unexpected error occurred');
    }
  }
};

export default AuthService;